import { Presentation } from '@features/powerpoint';
import { BuildReportOptions } from './build-presentation';
import { visibleSlide } from '@features/executive-report/slides';
import { titleSlide } from '@features/executive-report/slides/v2/title';
import { agendaSlide } from '@features/executive-report/slides/v2/agenda';
import { howWeDoItSlide } from '@features/executive-report/slides/v2/how-we-do-it';
import { coverSlide } from '@features/executive-report/slides/v2/cover';
import { campaignSummaryGiroasSlide } from '@features/executive-report/slides/v2/campaign-summary-giroas';
import { salesCampaignOverviewSlide } from '@features/executive-report/slides/v2/sales-campaign-overview';
import { navbarSections } from '../domain/navbar-sections';
import { buildNavbarText } from '@features/executive-report/slides/v2/components/navbar-texts';
import { salesCampaignOverviewSlide2 } from '@features/executive-report/slides/v2/sales-campaign-overview-2';
import { salesDimensionSlides } from '../slides/v2/sales-dimensions';
import { buildKeyDriversCards } from '../slides/v2/sales-campaign-overview-2/build-key-drivers-cards';

export type BuildReportOptionsV2 = Extract<
  BuildReportOptions,
  { designVersion: 'v2' }
>;

export const buildSlidesV2 = (
  options: BuildReportOptionsV2
): Presentation['slides'] => {
  return [
    ...visibleSlide({
      slide: titleSlide(
        options.campaign,
        options.brandLogo,
        options.customFields.campaignName
      ),
      visible: options.customFields.includedSlides.intro.title,
    }),
    ...visibleSlide({
      slide: agendaSlide([
        { title: 'The Who', text: ' - Audience' },
        { title: 'The What', text: ' - Creative' },
        { title: 'The Where', text: ' - Channels' },
        { title: 'The How', text: ' - Retailers where purchase happened' },
      ]),
      visible: options.customFields.includedSlides.intro.agenda,
    }),
    ...visibleSlide({
      slide: howWeDoItSlide(),
      visible: options.customFields.includedSlides.intro.howWeDoIt,
    }),
    ...visibleSlide({
      slide: coverSlide('Sales Lift'),
      visible: options.customFields.includedSlides.sales.cover,
    }),
    ...visibleSlide({
      slide: campaignSummaryGiroasSlide({
        flightDates: [options.campaign.startDate, options.campaign.endDate],
        campaignBudget: options.campaign.adSpend,
        impressions: options.report.impressions ?? 0,
        uniqueUsers: options.report.uniqueUsers ?? 0,
      }),
      visible: options.customFields.includedSlides.sales.campaignSummaryGiroas,
    }),
    ...visibleSlide({
      slide: salesCampaignOverviewSlide({
        navbarText: buildNavbarText(navbarSections, 'Overview'),
        adSpend: options.campaign.adSpend,
        flightDates: [options.campaign.startDate, options.campaign.endDate],
        measurementPeriod: [
          options.campaign.startDate,
          options.campaign.endDate,
        ],
        uniqueUsers: options.report.uniqueUsers ?? 0,
        impressions: options.report.impressions ?? 0,
        sections: [
          {
            name: 'Who',
            count: options.subdimensionCounts.aud ?? 0,
            dimensionName: 'Audience',
          },
          {
            name: 'What',
            count: options.subdimensionCounts.creative ?? 0,
            dimensionName: 'Creative',
          },
          {
            name: 'Where',
            count: options.subdimensionCounts.channel ?? 0,
            dimensionName: 'Channel',
          },
          {
            name: 'How',
            count: options.retailerCount,
            dimensionName: 'Retailers',
          },
        ],
      }),
      visible: options.customFields.includedSlides.sales.campaignOverview,
    }),
    ...visibleSlide({
      slide: salesCampaignOverviewSlide2({
        navbarText: buildNavbarText(navbarSections, 'Overview'),
        incrementalLift: options.report.incrementalSalesLift,
        incrementalSales: options.report.projectedIncrementalSales,
        iroas: options.roasData.iroas,
        objectives: options.customFields.salesObjectives.map(o => o.value),
        keyDrivers: buildKeyDriversCards({
          avgPricePerTransaction: options.report.averagePricePerTransaction,
          avgTotalBasketAmount: options.report.averageTotalBasketAmount,
          avgPurchaseFrequency: options.report.averagePurchaseFrequency,
        }),
      }),
      visible: options.customFields.includedSlides.sales.campaignOverview2,
    }),
    ...salesDimensionSlides(options),
  ];
};
